import * as Yup from "yup";
import React, {useCallback, useEffect, useState} from "react";
import _ from "lodash";
import {Field, FieldArray, FieldProps, Form, Formik, FormikHelpers} from "formik";
import {useDispatch, useSelector} from "react-redux";
import moment from "moment";
import {IRootReducerState} from "../../../store/reducers";
import {CommonService} from "../../../shared/services";
import {IAPIResponseType} from "../../../shared/models/api.model";
import {ImageConfig, Misc, Patterns} from "../../../constants";
import {setFormChangesData} from "../../../store/actions/form-state.action";
import FormikInputComponent from "../../../shared/components/form-controls/formik-input/FormikInputComponent";
import FormikDatePickerComponent
    from "../../../shared/components/form-controls/formik-date-picker/FormikDatePickerComponent";
import FormikSelectDropdownComponent
    from "../../../shared/components/form-controls/formik-select-dropdown/FormikSelectDropdownComponent";
import FormikSsnInputComponent from "../../../shared/components/form-controls/formik-ssn-input/FormikSsnInputComponent";
import ButtonComponent from "../../../shared/components/button/ButtonComponent";
import "./AuthorizationDiscloseProtectedHealthInformation.scss"
import FormikPhoneInputComponent from "../../../shared/components/formik-phone-input/FormikPhoneInputComponent";
import HorizontalLineComponent
    from "../../../shared/components/horizontal-line/horizontal-line/HorizontalLineComponent";
import FormikCheckBoxComponent from "../../../shared/components/form-controls/formik-check-box/FormikCheckBoxComponent";
import FormControlLabelComponent from "../../../shared/components/form-control-label/FormControlLabelComponent";
import ErrorComponent from "../../../shared/components/error/ErrorComponent";
import ESignApprovalComponent from "../../../shared/components/e-sign-approval/ESignApprovalComponent";
import LinkComponent from "../../../shared/components/link/LinkComponent";
import ModalComponent from "../../../shared/components/modal/ModalComponent";
import SignaturePopupComponent from "../../../shared/components/signature-popup/SignaturePopupComponent";
import {
    getClientAuthorizationDiscloseProtectedHealthInformation,
    getClientSignatureDetails
} from "../../../store/actions/client.action";
import SuccessPopupComponent from "../../../shared/components/success-popup/SuccessPopupComponent";
import {useNavigate} from "react-router-dom";
import IconButtonComponent from "../../../shared/components/icon-button/IconButtonComponent";
import FormDebuggerComponent from "../../../shared/components/form-debugger/FormDebuggerComponent";
import FormikRadioButtonGroupComponent
    from "../../../shared/components/form-controls/formik-radio-button-group/FormikRadioButtonGroupComponent";

interface AuthorizationDiscloseProtectedHealthInformationProps {
    onPrevious?: () => void;
    onNext: () => void;
    appointmentId: any
}


const familyObj = {
    name: "",
    relationship: "",
    phone: ""
}




const AuthorizationDiscloseProtectedHealthInformationValidationSchema = Yup.object({
    patient_name: Yup.string().required('Name is required'),
    patient_ssn: Yup.string().matches(/^\d{9}$/, 'SSN is not valid').nullable(),
    patient_phone: Yup.string().required('Phone is required'),
    patient_dob: Yup.string().required('Date is required'),
    patient_address: Yup.object({
        address_line: Yup.string().required('Address is required'),
        state: Yup.string().required('State is required'),
        city: Yup.string().required('City is required'),
        zip_code: Yup.string().required('Zip Code is required'),
        country: Yup.string().required('Country is required'),

    }),
    is_notice_of_privacy_acknowledged: Yup.boolean().oneOf([true], 'Please acknowledge to the consent'),
    agreed_to_disclose_information: Yup.boolean().required('This field is required').nullable(),
    information_disclosed_to: Yup.mixed().when('agreed_to_disclose_information', {
        is: true,  // Only apply validation when agreed_to_disclose_information is true
        then: () => Yup.array().of(
            Yup.object({
                name: Yup.string().required('Name is required'),
                phone: Yup.string().required('Phone number is required'),
                relationship: Yup.string().required('Relationship is required'),
            })
        ).min(1, 'At least one contact is required'),
        otherwise: () => Yup.mixed().notRequired(), // No validation when false
    }),
    client_name: Yup.string().required('Name is required'),
    date: Yup.string().nullable(),
    agreed_to_terms: Yup.boolean().oneOf([true], 'Please acknowledge to the consent'),
    is_authorized_representative: Yup.boolean(),
    representative_name: Yup.string().when("is_authorized_representative", {
        is: true,
        then: (schema) => schema.required('Authorized Representative Name is required')
    }),
    signature: Yup.string().required()
});

const AuthorizationDiscloseProtectedHealthInformationFormInitialValues = {
    patient_name: "",
    patient_dob: "",
    patient_ssn: "",
    patient_phone: "",
    patient_address: {
        address_line: "",
        city: "",
        state: "",
        zip_code: "",
        country:""
    },
    is_notice_of_privacy_acknowledged: false,
    information_disclosed_to: [
        {
            name: '',
            phone: '',
            relationship: '',
        }
    ],
    client_name: "",
    date: new Date(),
    agreed_to_terms: false,
    is_authorized_representative: false,
    representative_name: "",
    signature: "",
    agreed_to_disclose_information: false,
}

const AuthorizationDiscloseProtectedHealthInformation = (props: AuthorizationDiscloseProtectedHealthInformationProps) => {
        const {onNext, appointmentId} = props;
        const [isPrivacyPolicyModalOpen, setIsPrivacyPolicyModalOpen] = useState<boolean>(false);
        const {systemSettingsDetails} = useSelector((state: IRootReducerState) => state.user)
        const navigate = useNavigate();
        const [isFormSubmissionSuccessful, setIsFormSubmissionSuccessful] = useState<boolean>(false);
        const {currentUser} = useSelector((state: IRootReducerState) => state.account);
        const [authorizationDiscloseProtectedHealthInformationInitial, setAuthorizationDiscloseProtectedHealthInformationFormInitialValues] = useState(_.cloneDeep(AuthorizationDiscloseProtectedHealthInformationFormInitialValues));
        const formikRef = React.useRef<any>(null);
        const [isSignaturePopupOpen, setIsSignaturePopupOpen] = useState<boolean>(false);
        const {clientSignatureDetails} = useSelector((state: IRootReducerState) => state.client);
        const {
            relationshipList
        } = useSelector((state: IRootReducerState) => state.staticData);

        const {
            authorizationDiscloseProtectedHealthInformation,
            clientBasicDetails,
            isClientBasicDetailsLoaded,
            isAuthorizationDiscloseProtectedHealthInformationLoaded
        } = useSelector((state: IRootReducerState) => state.client);

        const dispatch = useDispatch();

        useEffect(() => {
            dispatch(getClientSignatureDetails(true, appointmentId));
        }, [dispatch, appointmentId]);

        useEffect(() => {
            if (isClientBasicDetailsLoaded && authorizationDiscloseProtectedHealthInformation) {
                const modifiedClientBasicDetails = _.cloneDeep(authorizationDiscloseProtectedHealthInformation) || {};
                modifiedClientBasicDetails.date = new Date();
                setAuthorizationDiscloseProtectedHealthInformationFormInitialValues(modifiedClientBasicDetails);
            } else {
                const modifiedClientBasicDetails = _.cloneDeep(AuthorizationDiscloseProtectedHealthInformationFormInitialValues);
                modifiedClientBasicDetails.client_name = `${clientBasicDetails?.first_name} ${clientBasicDetails?.last_name}`;
                modifiedClientBasicDetails.date = new Date();

                if (!authorizationDiscloseProtectedHealthInformation) {
                    modifiedClientBasicDetails.patient_name = `${clientBasicDetails?.first_name} ${clientBasicDetails?.last_name}`;
                    modifiedClientBasicDetails.patient_ssn = clientBasicDetails?.ssn;
                    modifiedClientBasicDetails.patient_phone = clientBasicDetails?.primary_contact_info?.phone;
                    modifiedClientBasicDetails.patient_dob = clientBasicDetails?.dob;
                    modifiedClientBasicDetails.patient_address = {
                        address_line: clientBasicDetails?.address?.address_line,
                        zip_code: clientBasicDetails?.address?.zip_code,
                        state: clientBasicDetails?.address?.state,
                        city: clientBasicDetails?.address?.city,
                        country: clientBasicDetails?.address?.country,
                    };
                }
                setAuthorizationDiscloseProtectedHealthInformationFormInitialValues(modifiedClientBasicDetails);
            }

            if (currentUser) {
                setAuthorizationDiscloseProtectedHealthInformationFormInitialValues((prevValues) => {
                    const newInitialValues = {
                        ...prevValues,
                        client_name: `${currentUser.first_name} ${currentUser.last_name}`,
                        date: new Date(),

                    };

                    if (clientSignatureDetails) {
                        newInitialValues.agreed_to_terms = clientSignatureDetails.agreed_to_terms;
                        newInitialValues.representative_name = clientSignatureDetails.representative_name;
                        newInitialValues.is_authorized_representative = clientSignatureDetails.is_authorized_representative ?? false;
                        newInitialValues.signature = clientSignatureDetails.signature;
                        newInitialValues.date = new Date();
                    }

                    return newInitialValues;
                });
            }
        }, [
            isClientBasicDetailsLoaded,
            isAuthorizationDiscloseProtectedHealthInformationLoaded,
            authorizationDiscloseProtectedHealthInformation,
            clientBasicDetails,
            currentUser,
            clientSignatureDetails,
            formikRef
        ]);
        const onSubmit = useCallback((values: any, {setSubmitting, setErrors}: FormikHelpers<any>) => {

            const modifiedValue = {
                ...values,
                appointment_id: appointmentId ? appointmentId : currentUser?.appointment_id,
            };
            // Check and format the dob field
            if (modifiedValue.parent_dob) {
                const parent_dob = moment(modifiedValue.parent_dob);
                if (parent_dob.isValid()) {
                    modifiedValue.parent_dob = parent_dob.format('YYYY-MM-DD');
                } else {
                    console.error('Invalid dob format:', modifiedValue.parent_dob);
                    setErrors({parent_dob: 'Invalid date of birth format'});
                    setSubmitting(false);
                    return;
                }
            }
            setSubmitting(true);
            CommonService._client.postAuthorizationDiscloseProtectedHealthInformation(modifiedValue)
                .then((response: IAPIResponseType<any>) => {
                    CommonService._alert.showToast(response[Misc.API_RESPONSE_MESSAGE_KEY], "success");
                    setSubmitting(false);
                    if (systemSettingsDetails?.other_settings?.waiver_enabled && !currentUser?.is_profile_completed) {
                        onNext();
                        dispatch(setFormChangesData(false));
                        dispatch(setFormChangesData(true));
                        dispatch(getClientAuthorizationDiscloseProtectedHealthInformation(currentUser?.appointment_id))
                    } else {
                        setIsFormSubmissionSuccessful(true);
                    }
                })
                .catch((error: any) => {
                    CommonService.handleErrors(setErrors, error);
                    setSubmitting(false);
                })
        }, [onNext, dispatch, systemSettingsDetails, currentUser, appointmentId]);


        const handleSignaturePopupClose = useCallback(() => {
            setIsSignaturePopupOpen(false);
        }, []);

        useEffect(() => {
            dispatch(getClientAuthorizationDiscloseProtectedHealthInformation(currentUser?.appointment_id))
        }, [dispatch, currentUser]);


        return (
            <div className={'authorization-disclose-protected-health-information-component'}>
                <div className="d-flex justify-content-center align-items-center form-header mrg-bottom-25" onClick={() => {
                    dispatch(getClientAuthorizationDiscloseProtectedHealthInformation(currentUser?.appointment_id))
                    console.log(clientBasicDetails)
                }}>
                    Authorization to Disclose Protected<br/>
                    Health Information
                </div>
                <Formik
                    validationSchema={AuthorizationDiscloseProtectedHealthInformationValidationSchema}
                    initialValues={authorizationDiscloseProtectedHealthInformationInitial}
                    validateOnChange={false}
                    validateOnBlur={true}
                    enableReinitialize={true}
                    validateOnMount={true}
                    innerRef={formikRef}
                    onSubmit={onSubmit}
                >
                    {({setValues, values, setFieldValue, validateForm, isValid, isSubmitting, touched, errors, dirty}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                            if (dirty) {
                                dispatch(setFormChangesData(true));

                            }
                        }, [validateForm, values, dirty]);
                        return (
                            <Form className="t-form" noValidate={true}>
                                <FormDebuggerComponent values={values} showDebugger={true} errors={errors}/>
                                <div className="t-form-controls">
                                    <div className={'forms-description-text-information justified-text mrg-bottom-20'}>
                                        This form must be fully completed in order to process your request. Your
                                        Protected Health Information
                                        (PHI) will only be disclosed by Kinergy Sports Medicine and Performance
                                        (hereafter known as KINERGY) to
                                        the person/entity listed below and will not be shared with any other individuals
                                        without your consent,
                                        unless required or permitted by law or as outlined in our Notice of Privacy
                                        Practices.
                                    </div>
                                    <div className={'form-sub-header mrg-bottom-20'} onClick={() => {
                                        console.log(errors);
                                        console.log(values)
                                    }}>Part 1: Client Information
                                    </div>
                                    <div className="ts-row">
                                        <div className="ts-col-lg-6 ts-col-md-6 ts-col-sm-12">
                                            <Field name={'patient_name'} className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            id={"name_input"}
                                                            label={"Patient Name"}
                                                            placeholder={"Enter Client's Name"}
                                                            required={true}
                                                            formikField={field}
                                                            fullWidth={true}
                                                            titleCase={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                        <div className="ts-col-lg-6 ts-col-md-6 ts-col-sm-12">
                                            <Field name={'patient_ssn'} className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikSsnInputComponent
                                                            id={'ssn_input'}
                                                            label={'Social Security Number'}
                                                            placeholder={'Enter SSN Number'}
                                                            required={false}
                                                            formikField={field}
                                                            fullWidth={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="ts-row">
                                        <div className="ts-col-lg-6 ts-col-md-6 ts-col-sm-12">
                                            <Field
                                                name={'patient_phone'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikPhoneInputComponent
                                                            id={"p_phone_number"}
                                                            label={'Phone Number'}
                                                            placeholder={'Enter Phone Number'}
                                                            required={true}
                                                            formikField={field}
                                                            fullWidth={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                        <div className="ts-col-lg-6 ts-col-md-6 ts-col-sm-12">
                                            <Field name={'patient_dob'} className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikDatePickerComponent
                                                            id={'dob_input'}
                                                            label={'Date of Birth'}
                                                            placeholder={'Select Date of Birth'}
                                                            required={true}
                                                            formikField={field}
                                                            fullWidth={true}
                                                            // clearable={true}
                                                            maxDate={moment()}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="ts-row">
                                        <div className="ts-col-lg-12 ts-col-md-6 ts-col-sm-12">
                                            <Field name={'patient_address.address_line'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Address Line'}
                                                            placeholder={'Enter Your Street Address'}
                                                            required={true}
                                                            formikField={field}
                                                            fullWidth={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="ts-row">
                                        <div className="ts-col-lg-6 ts-col-md-6 ts-col-sm-12">
                                            <Field name={'patient_address.city'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'City'}
                                                            placeholder={'Enter City'}
                                                            required={true}
                                                            formikField={field}
                                                            fullWidth={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                        <div className="ts-col-lg-6 ts-col-md-6 ts-col-sm-12">
                                            <Field name={'patient_address.state'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'State'}
                                                            placeholder={'Enter State'}
                                                            required={true}
                                                            formikField={field}
                                                            fullWidth={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="ts-row">

                                        <div className="ts-col-lg-6 ts-col-md-6 ts-col-sm-12">
                                            <Field name={'patient_address.zip_code'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'ZIP Code'}
                                                            validationPattern={Patterns.FIVE_DIGITS_ONLY}
                                                            placeholder={'Enter ZIP Code'}
                                                            required={true}
                                                            formikField={field}
                                                            fullWidth={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                        <div className={'ts-col-lg-6'}>
                                            <Field name={'patient_address.country'}
                                                   className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Country'}
                                                            placeholder={'Enter your Country'}
                                                            formikField={field}
                                                            required={true}
                                                            fullWidth={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    <HorizontalLineComponent className={'pdd-left-5'}/>
                                    <div className={'d-flex justify-content-space-between'}>
                                        <div className={'form-sub-header mrg-bottom-20'}>Part 2: Notice of Privacy
                                            Practices
                                        </div>
                                        <div className={"underline-text cursor-pointer"}
                                             onClick={() => setIsPrivacyPolicyModalOpen(true)}>View Notice
                                        </div>
                                    </div>

                                    <div className="ts-row">
                                        <div className="ts-col-12">
                                            <Field name={'is_notice_of_privacy_acknowledged'}
                                                   className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikCheckBoxComponent
                                                            label={<div className={'notice-practice-text'}>I acknowledge
                                                                that I have been granted access to and
                                                                had the opportunity to review KINERGY's Notice of
                                                                Privacy
                                                                Practices, which explains how my protected health
                                                                information may be used and disclosed for treatment,
                                                                training, payment, healthcare operations, and other
                                                                permitted purposes. I understand that this notice is
                                                                available to me via my Client Portal or can be viewed
                                                                online
                                                                at any time. I understand that if I have any questions
                                                                or
                                                                concerns, I can contact the Privacy Officer listed in
                                                                the
                                                                notice. Additionally, I understand that my information
                                                                may
                                                                be disclosed electronically by KINERGY and/or its staff.
                                                                To
                                                                the extent permitted by law, I consent to the use and
                                                                disclosure of my protected health information as
                                                                outlined in
                                                                KINERGY's Notice of Privacy Practices.</div>}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    <HorizontalLineComponent className={'pdd-left-5'} marginTop={false}/>

                                    <div className={'form-sub-header mrg-bottom-20'}>Part 3: Disclosures to Friends
                                        and/or
                                        Family Members
                                    </div>
                                    <div className={'part-3-question-text mrg-bottom-20'}>
                                        Would you like to designate an individual or entity with whom KINERGY's staff
                                        may
                                        discuss your Protected Health Information?
                                    </div>
                                    <div className="ts-row">
                                        <div className="ts-col-12">
                                            <Field name={'agreed_to_disclose_information'}
                                                   className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikRadioButtonGroupComponent
                                                            direction={'row'}
                                                            options={CommonService._staticData.options}
                                                            displayWith={(option) => option.label}
                                                            valueExtractor={(option) => option.value}
                                                            formikField={field}
                                                            required={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    {(values?.agreed_to_disclose_information) && <div>
                                        <div className={'disclose-text mrg-bottom-30'}>You may disclose this information
                                            to:
                                        </div>
                                        <FieldArray
                                            name="information_disclosed_to"
                                            render={(arrayHelpers) => (
                                                <>
                                                    {values?.information_disclosed_to && values?.information_disclosed_to?.map((item: any, index: any) => {
                                                        return (
                                                            <div>
                                                                {values?.information_disclosed_to?.length > 1 && <div
                                                                    className={'mrg-bottom-30 d-flex justify-content-space-between align-items-center'}>
                                                                    <div
                                                                        className={'contact-counting-text'}>Contact {index + 1}:
                                                                    </div>
                                                                    <ButtonComponent variant={"outlined"}
                                                                                     color={"error"}
                                                                                     size={"small"}
                                                                                     prefixIcon={
                                                                                         <ImageConfig.DeleteIcon/>}
                                                                                     onClick={() => {
                                                                                         arrayHelpers.remove(index);
                                                                                     }}
                                                                    >
                                                                        Delete
                                                                    </ButtonComponent>
                                                                </div>}
                                                                <div className="ts-row" key={index}>
                                                                    <div
                                                                        className="ts-col-lg-12 ts-col-md-12 ts-col-sm-12">
                                                                        <Field
                                                                            name={`information_disclosed_to[${index}].name`}
                                                                            className="t-form-control">
                                                                            {
                                                                                (field: FieldProps) => (
                                                                                    <FormikInputComponent
                                                                                        id={"name_input"}
                                                                                        label={"Full Name"}
                                                                                        placeholder={"E.g., John Mirelyn Doe"}
                                                                                        required={true}
                                                                                        formikField={field}
                                                                                        fullWidth={true}
                                                                                        titleCase={true}
                                                                                    />
                                                                                )
                                                                            }
                                                                        </Field>
                                                                    </div>
                                                                </div>
                                                                <div className="ts-row">
                                                                    <div
                                                                        className="ts-col-lg-6 ts-col-md-6 ts-col-sm-12">
                                                                        <Field
                                                                            name={`information_disclosed_to[${index}].relationship`}>
                                                                            {
                                                                                (field: FieldProps) => (
                                                                                    <FormikSelectDropdownComponent
                                                                                        options={relationshipList}
                                                                                        label={'Relationship'}
                                                                                        placeholder={'Select Relationship'}
                                                                                        formikField={field}
                                                                                        fullWidth={true}
                                                                                        isClearable={false}
                                                                                        required={true}
                                                                                    />
                                                                                )
                                                                            }
                                                                        </Field>
                                                                    </div>
                                                                    <div
                                                                        className="ts-col-lg-6 ts-col-md-6 ts-col-sm-12">
                                                                        <Field
                                                                            name={`information_disclosed_to[${index}].phone`}>
                                                                            {
                                                                                (field: FieldProps) => (
                                                                                    <FormikPhoneInputComponent
                                                                                        label={'Phone Number'}
                                                                                        required={true}
                                                                                        placeholder={'Enter Phone Number'}
                                                                                        formikField={field}
                                                                                        fullWidth={true}
                                                                                    />
                                                                                )
                                                                            }
                                                                        </Field>
                                                                    </div>
                                                                </div>
                                                                {(index > -1 && index < values?.information_disclosed_to?.length - 1) &&
                                                                    <HorizontalLineComponent className={'pdd-left-5'}/>}
                                                            </div>
                                                        )
                                                    })}
                                                    {values?.information_disclosed_to?.length < 10 &&
                                                        <ButtonComponent variant={"contained"}
                                                                         className={'add-another-contact-disclose-button'}
                                                                         prefixIcon={<ImageConfig.AddIcon width={4}
                                                                                                          height={4}/>}
                                                                         onClick={() => {
                                                                             arrayHelpers.push(familyObj);
                                                                         }}
                                                        >
                                                            Add Another Contact
                                                        </ButtonComponent>}
                                                </>
                                            )}/>
                                        <div className={'mrg-top-25 mrg-bottom-10 contact-counting-text'}>Purpose of
                                            Disclosure:
                                        </div>
                                        <div className={'forms-description-text-information justified-text'}>
                                            I understand that the purpose of this authorization is to provide relevant
                                            pieces of my Protected Health Information (PHI) to the individual(s) listed
                                            above, to facilitate communication, coordination, and continuity of care. I
                                            acknowledge that my information may not be disclosed to any other individual
                                            or
                                            entity by KINERGY without my expressed consent, unless required or permitted
                                            by
                                            law or as outlined in our Notice of Privacy Practices.
                                        </div>
                                        <div className={'mrg-top-25 mrg-bottom-10 contact-counting-text'}>
                                            Expiration Date of Authorization:
                                        </div>
                                        <div
                                            className={'forms-description-text-information mrg-bottom-20 justified-text'}>
                                            This authorization will remain valid for one (1) year from the date of
                                            signing.
                                        </div>
                                    </div>}
                                    <HorizontalLineComponent className={'pdd-left-5'}/>
                                    <div className={'form-sub-header mrg-bottom-20'}>Part 4: Client Acknowledgment and
                                        Rights
                                    </div>
                                    <div className="health-list mrg-left-5 mrg-bottom-20">
                                        <div className="health-item">
                                            I acknowledge that this authorization is voluntary.
                                        </div>

                                        <div className="health-item">
                                            I understand that by granting this authorization, the recipient of my
                                            protected
                                            health information (PHI) may not be subject to the same privacy protections
                                            under HIPAA, and they may disclose this information to others, with or
                                            without
                                            my consent. Once my information is disclosed by KINERGY, it may no longer be
                                            protected by HIPAA or other privacy laws.
                                        </div>

                                        <div className="health-item">
                                            I understand that I may revoke this authorization at any time prior to its
                                            expiration by submitting written notice of revocation to the entity
                                            providing
                                            the information. I understand I cannot revoke this authorization
                                            retroactively
                                            for information already released.
                                        </div>

                                        <div className="health-item"> I understand that I have the right to access and
                                            copy
                                            the information described on this form upon request.
                                        </div>
                                    </div>
                                    <HorizontalLineComponent className={'pdd-left-5'}/>

                                    <div className="ts-row">
                                        <div className="ts-col-12">
                                            <Field name={'agreed_to_terms'} className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikCheckBoxComponent
                                                            label={<>I certify that I have read and fully understand the
                                                                above statements and consent fully and voluntarily to its
                                                                contents.</>}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="ts-row">
                                        <div className="ts-col-12">
                                            <Field name={'is_authorized_representative'}
                                                   className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikCheckBoxComponent
                                                            label={'I am an Authorized Representative for the client (ie. Parent, Legal Guardian, Agent, Manager, etc.)'}
                                                            formikField={field}
                                                            onChange={() => {
                                                                setFieldValue('representative_name', '');
                                                            }}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="ts-row">
                                        <div className="ts-col-md-12 ts-col-lg-6">
                                            <Field name={'client_name'} className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            id={"name_input"}
                                                            label={'Client Name'}
                                                            placeholder={'Enter your name'}
                                                            required={true}
                                                            disabled={true}
                                                            formikField={field}
                                                            fullWidth={true}
                                                            titleCase={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                        <div className="ts-col-md-12 ts-col-lg-6">
                                            <Field name={'date'} className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikDatePickerComponent
                                                            id={"dob_input"}
                                                            label={'Date'}
                                                            placeholder={'Enter Date'}
                                                            formikField={field}
                                                            fullWidth={true}
                                                            disabled={true}
                                                            readOnly={true}
                                                            // clearable={false}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    {
                                        values?.is_authorized_representative && <div className="ts-row">
                                            <div className="ts-col-12">
                                                <Field name={'representative_name'} className="t-form-control">
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Authorized Representative Name'}
                                                                placeholder={'Enter Representative Name'}
                                                                required={true}
                                                                formikField={field}
                                                                fullWidth={true}
                                                                titleCase={true}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                        </div>
                                    }
                                    {!(values?.signature && authorizationDiscloseProtectedHealthInformationInitial?.signature) &&
                                        <div className="ts-row">
                                            <div className="ts-col-12">
                                                <FormControlLabelComponent
                                                    className={"font-weight-thin"}
                                                    label={"Signature of Client or Authorized Representative"}
                                                    required={true}/>
                                                <ButtonComponent
                                                    className={'add-signature-button'}
                                                    onClick={() => {
                                                        setIsSignaturePopupOpen(true);
                                                    }}
                                                    prefixIcon={<ImageConfig.AddCircleFilled/>}
                                                    variant={'link'}
                                                    color={'info'}
                                                    size={'medium'}
                                                >
                                                    Add Signature
                                                </ButtonComponent>
                                            </div>
                                            {
                                                (_.get(touched, "signature") && !!(_.get(errors, "signature"))) &&
                                                <div className={'mrg-left-15'}>
                                                    <ErrorComponent
                                                        errorText={(_.get(errors, "signature"))}/>
                                                </div>
                                            }
                                        </div>
                                    }
                                    {(values?.signature && authorizationDiscloseProtectedHealthInformationInitial?.signature) &&
                                        <>
                                            <ESignApprovalComponent isSigned={true}
                                                                    signature_url={values?.signature}
                                            />
                                            <div className={'signature-remove-button'}>
                                                <LinkComponent onClick={() => {
                                                    setFieldValue('signature', "");
                                                }
                                                }>
                                                    Remove Signature
                                                </LinkComponent>
                                            </div>
                                        </>
                                    }
                                    <div className="t-form-actions">
                                        <ButtonComponent
                                            id={'next_btn'}
                                            size={'large'}
                                            type={"submit"}
                                            suffixIcon={<ImageConfig.ArrowRightCircle/>}
                                            disabled={!isValid || isSubmitting}
                                            isLoading={isSubmitting}
                                        >
                                            Next
                                        </ButtonComponent>
                                    </div>


                                </div>
                            </Form>
                        );
                    }}
                </Formik>
                <ModalComponent isOpen={isSignaturePopupOpen}
                                onClose={handleSignaturePopupClose}
                                size={"xl"}
                >
                    <SignaturePopupComponent
                        onSign={(signImage: any) => {
                            formikRef?.current.setFieldValue('signature', signImage)
                            setAuthorizationDiscloseProtectedHealthInformationFormInitialValues({
                                ...formikRef?.current?.values,
                                signature: signImage
                            })
                            handleSignaturePopupClose();
                        }}
                        handleSignaturePopupClose={handleSignaturePopupClose}
                    />
                </ModalComponent>
                <ModalComponent isOpen={isPrivacyPolicyModalOpen}
                                className="privacy-policy-modal"
                                onClose={() => setIsPrivacyPolicyModalOpen(false)}>
                    <div className="privacy-policy-modal__content">
                        <div className="privacy-policy-modal__content_left">
                            <div className="privacy-policy-modal__content_icon">
                                <ImageConfig.PrivacyPolicyIcon/>
                            </div>
                            <div className="privacy-policy-modal__content_title">
                                Notice of Privacy<br/>
                                Practices
                            </div>
                        </div>
                        <div className="privacy-policy-modal__content_right onboarding-privacy-policy-modal">
                            <div className="privacy-policy-modal__content_right__header">
                                <IconButtonComponent id={'close_btn'} onClick={
                                    () => setIsPrivacyPolicyModalOpen(false)
                                }>
                                    <ImageConfig.CloseIcon/>
                                </IconButtonComponent>
                            </div>
                            <div className="privacy-policy-modal__content_right__block">
                                <div className="privacy-policy-modal__content_right__block__title">
                                    Our Notice of Privacy Practices
                                </div>
                                <div className="privacy-policy-modal__content_right__block__content">
                                    At <b>Kinergy Sports Medicine and Performance,</b> we respect and are committed to
                                    protecting your privacy and ensuring that your personal health information is
                                    handled with the utmost care and confidentiality. As part of this commitment, the
                                    collection, use, and disclosure of certain personal and health information about you
                                    are governed by the Health Insurance Portability and Accountability Act of 1996
                                    (HIPAA) and applicable state law. Any information you provide to us that qualifies
                                    as Protected Health Information (PHI), as defined by HIPAA, will be handled
                                    according to HIPAA guidelines and relevant state regulations.

                                    <br/> <br/>
                                    PHI refers to individually identifiable health information related to your past,
                                    present, or future physical or mental health conditions. This <b>Notice of Privacy
                                    Practices</b> explains how your PHI may be used and disclosed, as well as how you
                                    can
                                    access and control this information.
                                </div>
                                <div className="health-info">
                                    <div className="health-title mrg-bottom-20">1. Uses and Disclosures of Health
                                        Information
                                    </div>
                                    <div className="health-content mrg-bottom-20">
                                        We may use and disclose your health information for the following purposes:
                                    </div>

                                    <div className="health-list mrg-left-20">
                                        <div className="health-item">
                                            <span className="bold">Treatment:</span> We may use your health information
                                            to provide, coordinate, or manage any of your cases. This may involve
                                            sharing your information with other healthcare providers, such as physicians
                                            or specialists, involved in your care.
                                        </div>

                                        <div className="health-item">
                                            <span className="bold">Payment:</span> We may use or disclose your
                                            information to bill and collect payment for the services provided to you.
                                            This may include sending your health information to insurance companies or
                                            other third-party payers.
                                        </div>

                                        <div className="health-item">
                                            <span className="bold">Healthcare Operations:</span> We may use your
                                            information for administrative purposes, such as quality control,
                                            improvement of services, and training of staff.
                                        </div>

                                        <div className="health-item">
                                            <span className="bold">Appointment Reminders:</span> We may use your health
                                            information to contact you for appointment reminders or follow-up care.
                                        </div>

                                        <div className="health-item">
                                            <span className="bold">Legal Requirements:</span> We may disclose your
                                            health information when required by law, such as in response to subpoenas,
                                            court orders, or government investigations.
                                        </div>
                                    </div>
                                </div>
                                <div className="health-info">
                                    <div className="health-title mrg-bottom-20 mrg-top-20">2. Your Rights Regarding
                                        Health
                                        Information
                                    </div>
                                    <div className="health-content mrg-bottom-20">
                                        You have the following rights concerning your health information:
                                    </div>

                                    <div className="health-list mrg-left-20">
                                        <div className="health-item">
                                            <span className="bold">Right to Access:</span> You have the right to
                                            request and inspect copies of your medical records. We may charge a fee for
                                            the cost of copying and mailing.
                                        </div>

                                        <div className="health-item">
                                            <span className="bold">Right to Correct Information:</span> If you believe
                                            any information in your medical record is incorrect or incomplete, you have
                                            the right to request that it be corrected.
                                        </div>

                                        <div className="health-item">
                                            <span className="bold">Right to Request Restrictions:</span> You have the
                                            right to request restrictions on how your health information is used or
                                            disclosed. While we are not required to agree to all requests, we will
                                            consider them.
                                        </div>

                                        <div className="health-item">
                                            <span className="bold">Right to Confidential Communications:</span> You have
                                            the right to request that we communicate with you about your health
                                            information in a certain way or at a certain location (e.g., by phone
                                            instead of mail).
                                        </div>

                                        <div className="health-item">
                                            <span className="bold">Right to an Accounting of Disclosures:</span> You
                                            have the right to request a list of disclosures of your health information
                                            made by us for purposes other than treatment, payment, and healthcare
                                            operations.
                                        </div>

                                        <div className="health-item">
                                            <span className="bold">Right to a Copy of This Notice:</span> You may
                                            request a copy of this Notice of Privacy Practices at any time, even if you
                                            have already received one.
                                        </div>
                                    </div>
                                </div>

                                <div className="health-info">
                                    <div className="health-title mrg-bottom-20 mrg-top-20">3. How We Protect Your
                                        Information
                                    </div>
                                    <div className="health-content mrg-bottom-20">
                                        We are committed to securing your personal health information. We use a variety
                                        of security measures, such as encryption and secure access protocols, to protect
                                        your information from unauthorized access or disclosure. We also limit access to
                                        your health information to those employees and partners who need it to perform
                                        their job duties.
                                    </div>
                                </div>
                                <div className="health-info">
                                    <div className="health-title mrg-bottom-20 mrg-top-20">4. Changes to This Notice
                                    </div>
                                    <div className="health-content mrg-bottom-20">
                                        We may change the terms of this Notice at any time. Any changes will be
                                        effective immediately upon updating this notice in our clinic and on our
                                        website. You will also receive a copy of the revised notice through your Client
                                        Portal.
                                    </div>
                                </div>
                                <div className="health-info">
                                    <div className="health-title mrg-bottom-20 mrg-top-20">5. Filing a Complaint
                                    </div>
                                    <div className="health-content mrg-bottom-20">
                                        If you believe that your privacy rights have been violated, you may file a
                                        complaint with us or with the U.S. Department of Health and Human Services. We
                                        will not retaliate against you for filing a complaint.
                                    </div>
                                    <div className="health-content mrg-bottom-20">
                                        To file a complaint or if you have questions about this Notice, please contact:
                                    </div>
                                    <div className="health-content">
                                        <div className={'bold'}>Terrill Lobo</div>
                                        <div className="health-content">
                                            Owner, Kinergy Sports Medicine and Performance
                                        </div>
                                        <div className="health-content">
                                            Phone: {CommonService.formatPhoneNumber(systemSettingsDetails?.other_settings?.admin_phone)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </ModalComponent>
                <ModalComponent isOpen={isFormSubmissionSuccessful}
                                size={'xs'}
                                closeOnBackDropClick={false}
                                className=""
                                onClose={() => setIsFormSubmissionSuccessful(false)}>
                    <div className="modal__content">
                        <SuccessPopupComponent
                            title={'All Done!'}
                            description={"Congratulations, you have completed your\n" +
                                "Portal Registration."}
                            imageData={ImageConfig.Tick}
                            actions={<>
                                <div className={'success-popup-continue-and-cancel-cta'}>
                                    <ButtonComponent onClick={() => navigate(CommonService._routeConfig.AppointmentList())}>
                                        Proceed
                                    </ButtonComponent>
                                </div>
                            </>}
                        />
                    </div>
                </ModalComponent>
            </div>
        )
            ;

    }
;

export default AuthorizationDiscloseProtectedHealthInformation;
